import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useSelector } from "react-redux";
import {
  FolderKanban,
  User,
  X,
  Wallet,
} from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import FilteredDataTable from "../../components/dashboard/FilteredDataTable";
import StatCard from "../../components/dashboard/StatCard";
import StatCardGrid from "../../components/dashboard/StatCardGrid";
import { USE_MOCK_AUTH } from "../../config/api";
import { CASES_FILTER_CONFIG } from "../../config/tableFilterConfigs";
import { MOCK_CASES, formatCurrency } from "../../data/mock/dashboardData";
import {
  EMPTY_CASES_SUMMARY,
  fetchAllProviderCases,
  fetchCasesSummary,
} from "../../services/casesApi";
import { fetchDebtCustomerCases } from "../../services/debtCustomersApi";
import { personNameColumns } from "../../utils/personName";
import { showError } from "../../utils/toast";
import { useLiveEventTick } from "../../hooks/useLiveRefresh";

function enrichMockCase(row) {
  return {
    ...row,
    outstandingAmount: Math.max(0, row.amountOwed - row.amountPaid),
    institution: row.institution ?? "Your provider",
    escalated: Boolean(row.escalated),
    createdAt: row.createdAt || row.updatedAt || null,
  };
}

function mockSummaryFromRows(rows) {
  return {
    totalCases: rows.length,
    totalOwed: rows.reduce((sum, row) => sum + (Number(row.amountOwed) || 0), 0),
    totalPaid: rows.reduce((sum, row) => sum + (Number(row.amountPaid) || 0), 0),
    totalEscalated: rows.filter((row) => row.escalated).length,
  };
}

export default function CasesPage() {
  const token = useSelector((state) => state.auth.token);
  const useLiveApi = Boolean(token) && !USE_MOCK_AUTH;
  const [searchParams] = useSearchParams();
  const debtCustomerSlug = searchParams.get("debtCustomer");
  const customerParam = searchParams.get("customer");
  const caseIdParam = searchParams.get("caseId");

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(EMPTY_CASES_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const liveTick = useLiveEventTick(/^(portfolio\.|case\.|payment\.)/);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!liveTick) setLoading(true);
      setLoadError(null);

      try {
        if (!useLiveApi) {
          const base = MOCK_CASES.map(enrichMockCase);
          const filtered = debtCustomerSlug || customerParam
            ? base.filter((c) => !customerParam || c.customer === customerParam)
            : base;
          if (!cancelled) {
            setRows(filtered);
            setSummary(mockSummaryFromRows(base));
          }
          return;
        }

        if (debtCustomerSlug) {
          const [casesResult, summaryResult] = await Promise.allSettled([
            fetchDebtCustomerCases(token, debtCustomerSlug),
            fetchCasesSummary(token),
          ]);

          if (cancelled) return;

          if (casesResult.status === "fulfilled") {
            setRows(casesResult.value || []);
          } else {
            throw casesResult.reason;
          }

          if (summaryResult.status === "fulfilled" && summaryResult.value) {
            setSummary(summaryResult.value);
          } else {
            setSummary(mockSummaryFromRows(casesResult.value || []));
          }
          return;
        }

        const [casesResult, summaryResult] = await Promise.allSettled([
          fetchAllProviderCases(token),
          fetchCasesSummary(token),
        ]);

        if (cancelled) return;

        if (casesResult.status === "fulfilled") {
          let nextRows = casesResult.value || [];
          if (customerParam) {
            const needle = customerParam.trim().toLowerCase();
            nextRows = nextRows.filter(
              (row) => String(row.customer || "").toLowerCase() === needle
            );
          }
          if (caseIdParam) {
            const needle = caseIdParam.trim().toLowerCase();
            nextRows = nextRows.filter(
              (row) =>
                String(row.id || "").toLowerCase() === needle ||
                String(row.case_id || "").toLowerCase() === needle
            );
          }
          setRows(nextRows);
        } else {
          throw casesResult.reason;
        }

        if (summaryResult.status === "fulfilled" && summaryResult.value) {
          setSummary(summaryResult.value);
        } else {
          setSummary(mockSummaryFromRows(casesResult.value || []));
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err.message || "Could not load collection cases.");
          showError(err.message || "Could not load collection cases.");
          setRows([]);
          setSummary(EMPTY_CASES_SUMMARY);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token, useLiveApi, debtCustomerSlug, customerParam, caseIdParam, liveTick]);

  const filterLabel = customerParam || (debtCustomerSlug ? "Selected customer" : null);

  const columns = useMemo(
    () => [
      { key: "numericId", header: "ID", type: "id" },
      { key: "caseId", header: "Case ID", type: "id" },
      { key: "customerNumber", header: "Customer no.", type: "phone" },
      ...personNameColumns(),
      { key: "accountNumber", header: "Account number", type: "id" },
      { key: "outstandingAmount", header: "Outstanding", type: "currency" },
      { key: "phone", header: "Mobile", type: "phone" },
      { key: "email", header: "Email" },
      { key: "ghana_card_number", header: "Ghana card", type: "id" },
      { key: "gender", header: "Gender" },
      { key: "date_of_birth", header: "Date of birth", type: "date" },
      { key: "country", header: "Country" },
      { key: "region", header: "Region" },
      { key: "exact_area", header: "Exact area" },
      { key: "gps_address", header: "GPS address" },
      { key: "loan_amount", header: "Loan amount", type: "currency" },
      { key: "amountOwed", header: "Amount owed", type: "currency" },
      { key: "amountPaid", header: "Amount paid", type: "currency" },
      { key: "dueDate", header: "Due date", type: "date" },
      { key: "last_payment_date", header: "Last payment", type: "date" },
      { key: "daysOverdue", header: "Days overdue", type: "number" },
      {
        key: "actions",
        header: "Actions",
        align: "center",
        headerClassName: "text-center",
        actions: (row) => [
          // Providers monitor recovery progress — SLA timeline is ops-only.
          // {
          //   key: "sla",
          //   label: "SLA timeline",
          //   description: "Upload → assign → contact → payment stages and call time",
          //   icon: Clock,
          //   tone: "sky",
          //   onClick: () => setSlaCaseId(row.id),
          // },
          {
            key: "payments",
            label: "View payments",
            icon: Wallet,
            tone: "emerald",
            to: row.customer
              ? `/dashboard/payments?customer=${encodeURIComponent(row.customer)}`
              : "/dashboard/payments",
          },
        ],
      },
    ],
    []
  );

  return (
    <PageShell
      title="Collection cases"
      description="Recovery cases created from validated portfolios. Collection and follow-up are managed by SimCollect — your team monitors progress here."
    >
      {filterLabel && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/10 bg-gradient-to-r from-primary/[0.04] to-accent/[0.06] px-4 py-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-primary shadow-sm">
              <User className="h-4 w-4" />
            </span>
            <div>
              <p className="text-xs font-semibold text-primary">Collection cases for</p>
              <p className="text-sm font-bold text-primary">{filterLabel}</p>
            </div>
          </div>
          <Link
            to="/dashboard/cases"
            className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-primary/70 ring-1 ring-primary/10 transition-colors hover:text-primary"
          >
            <X className="h-3.5 w-3.5" />
            Show all cases
          </Link>
        </div>
      )}

      <StatCardGrid cols={3} className="mb-4">
        <StatCard
          label="Total cases"
          value={summary.totalCases.toLocaleString()}
          icon={FolderKanban}
          tone="primary"
        />
        <StatCard
          label="Total owed"
          value={formatCurrency(summary.totalOwed)}
          icon={Wallet}
          tone="accent"
        />
        <StatCard
          label="Total paid"
          value={formatCurrency(summary.totalPaid)}
          icon={Wallet}
          tone="emerald"
        />
      </StatCardGrid>

      {loadError ? <p className="mb-3 text-sm text-rose-600">{loadError}</p> : null}
      {loading ? (
        <p className="mb-3 text-sm text-primary/45">Loading collection cases…</p>
      ) : (
        <FilteredDataTable
          key={`cases-${debtCustomerSlug ?? customerParam ?? "all"}`}
          data={rows}
          columns={columns}
          config={{ ...CASES_FILTER_CONFIG, title: "Case filters" }}
          pageSize={15}
          maxVisibleColumns={6}
          emptyMessage={
            debtCustomerSlug || customerParam
              ? "No collection cases found for this customer."
              : "No records match your filters."
          }
        />
      )}
    </PageShell>
  );
}
